import type { Ref } from 'react';
import { forwardRef, useMemo } from 'react';
import type { ListItemProps } from '@mui/material';
import { ListItem as MaterialListItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme, SxStyles } from '@/ui/style/types';
import { composeSxProps } from '@/ui/style/types';
import { logger } from '@/core/utils/logger';

/**
 * Gets custom sx classes for the list item.
 *
 * @param theme - The MUI theme object
 * @returns The sx classes object
 */
const getSxClasses = (theme: Theme): SxStyles => ({
  listItem: {
    color: 'text.primary',
    padding: theme.spacing(0),
  },
});

/**
 * Material-UI ListItem component for list content containers.
 *
 * Wraps Material-UI's ListItem to provide content container within lists.
 * Maintains full compatibility with Material-UI ListItem props. Use with
 * ListItemButton, ListItemIcon, and ListItemText for complete list item layouts.
 *
 * @param props - ListItem configuration (see MUI docs for all available props)
 * @param ref - Reference to the underlying li element
 * @returns ListItem component for list item content
 */
function ListItemUI(props: ListItemProps, ref: Ref<HTMLLIElement>): JSX.Element {
  logger.logTraceRenderDetailed('ui/list/list-item', props);

  // Extract sx prop to merge with internal styles
  const { children, sx, ...rest } = props;

  // Hook
  const theme = useTheme();

  /**
   * Builds custom sx classes for the list item.
   */
  const memoSxClasses = useMemo((): SxStyles => {
    // Log
    logger.logTraceUseMemo('LIST-ITEM - memoSxClasses', theme);
    return getSxClasses(theme);
  }, [theme]);

  // Compose internal and caller sx props without nesting array-form sx.
  const sxMerged = composeSxProps(memoSxClasses.listItem, sx);

  return (
    <MaterialListItem sx={sxMerged} {...rest} ref={ref}>
      {children !== undefined && children}
    </MaterialListItem>
  );
}

// Export the List Item using forwardRef so that passing ref is permitted and functional in the react standards
export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(ListItemUI);
